import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const Spreadsheet = ({
  initialData = [],
  onDataChange,
  readOnly = false,
  sheetName = "Sheet1"
}) => {
  const [data, setData] = useState(initialData);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [columnHeaders, setColumnHeaders] = useState([
    'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L',
  ]);
  const [rowCount, setRowCount] = useState(1000);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [contextMenuType, setContextMenuType] = useState('cell');
  const [selectedRange, setSelectedRange] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [copiedData, setCopiedData] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [pastePreview, setPastePreview] = useState(null);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);

  const spreadsheetRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const tableRef = useRef(null);

  // Initialize data if empty
  useEffect(() => {
    if (data.length === 0) {
      const newData = [];
      for (let i = 0; i < rowCount; i++) {
        const row = {};
        columnHeaders.forEach((col, index) => {
          row[col] = '';
        });
        newData.push(row);
      }
      setData(newData);
    }
  }, []);

  // Ensure data has enough rows
  useEffect(() => {
    if (data.length < rowCount) {
      const newData = [...data];
      for (let i = data.length; i < rowCount; i++) {
        const row = {};
        columnHeaders.forEach((col) => {
          row[col] = '';
        });
        newData.push(row);
      }
      setData(newData);
    }
  }, [rowCount, columnHeaders, data.length]);

  // Handle cell click with different selection modes
  const handleCellClick = (e, rowIndex, colKey) => {
    const cellKey = `${rowIndex}-${colKey}`;

    const ctrl = e?.ctrlKey || e?.metaKey || isCtrlPressed;
    const shift = e?.shiftKey || isShiftPressed;

    if (ctrl) {
      // Ctrl+Click: Toggle individual cell selection
      setSelectedCells(prev => {
        const newSet = new Set(prev);
        if (newSet.has(cellKey)) {
          newSet.delete(cellKey);
        } else {
          newSet.add(cellKey);
        }
        return newSet;
      });
      setSelectedCell({ row: rowIndex, col: colKey });
      setSelectedRange(null);
    } else if (shift && selectedCell) {
      // Shift+Click: Select range from previous cell to current cell
      const startRow = Math.min(selectedCell.row, rowIndex);
      const endRow = Math.max(selectedCell.row, rowIndex);
      const startColIndex = Math.min(columnHeaders.indexOf(selectedCell.col), columnHeaders.indexOf(colKey));
      const endColIndex = Math.max(columnHeaders.indexOf(selectedCell.col), columnHeaders.indexOf(colKey));

      setSelectedRange({
        start: { row: startRow, col: columnHeaders[startColIndex] },
        end: { row: endRow, col: columnHeaders[endColIndex] }
      });
      setSelectedCells(new Set());
    } else {
      // Normal click: Select single cell
      setSelectedCell({ row: rowIndex, col: colKey });
      setSelectedRange(null);
      setSelectedCells(new Set([cellKey]));
    }
  };

  // Handle cell double click to edit
  const handleCellDoubleClick = (rowIndex, colKey) => {
    if (readOnly) return;
    setEditingCell({ row: rowIndex, col: colKey });
    setEditValue(data[rowIndex]?.[colKey] || '');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Handle column header click (select entire column)
  const handleColumnHeaderClick = (colKey) => {
    const colIndex = columnHeaders.indexOf(colKey);
    setSelectedRange({
      start: { row: 0, col: colKey },
      end: { row: data.length - 1, col: colKey }
    });
    setSelectedCell({ row: 0, col: colKey });
    setSelectedCells(new Set());
  };

  // Handle row header click (select entire row)
  const handleRowHeaderClick = (rowIndex) => {
    setSelectedRange({
      start: { row: rowIndex, col: columnHeaders[0] },
      end: { row: rowIndex, col: columnHeaders[columnHeaders.length - 1] }
    });
    setSelectedCell({ row: rowIndex, col: columnHeaders[0] });
    setSelectedCells(new Set());
  };

  // Handle cell value change with debouncing
  const handleCellChange = useCallback((rowIndex, colKey, value) => {
    setData(prevData => {
      const newData = [...prevData];
      if (!newData[rowIndex]) {
        newData[rowIndex] = {};
      }
      newData[rowIndex][colKey] = value;
      return newData;
    });

    // Debounced callback to parent
    setTimeout(() => {
      onDataChange?.(data);
    }, 100);
  }, [onDataChange, data]);

  // Handle input blur
  const handleInputBlur = () => {
    if (editingCell) {
      handleCellChange(editingCell.row, editingCell.col, editValue);
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      // Move to next row
      if (editingCell && editingCell.row < data.length - 1) {
        setSelectedCell({ row: editingCell.row + 1, col: editingCell.col });
        setSelectedRange(null);
        setSelectedCells(new Set([`${editingCell.row + 1}-${editingCell.col}`]));
      }
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      handleInputBlur();
      // Move to next/previous column
      const currentColIndex = columnHeaders.indexOf(editingCell.col);
      const nextColIndex = e.shiftKey
        ? Math.max(0, currentColIndex - 1)
        : Math.min(columnHeaders.length - 1, currentColIndex + 1);
      const nextCol = columnHeaders[nextColIndex];
      setSelectedCell({ row: editingCell.row, col: nextCol });
      setSelectedRange(null);
      setSelectedCells(new Set([`${editingCell.row}-${nextCol}`]));
    }
  };

  // Add new row
  const addRow = () => {
    const newRow = {};
    columnHeaders.forEach(col => {
      newRow[col] = '';
    });
    setData([...data, newRow]);
    setRowCount(rowCount + 1);
  };

  // Delete selected row
  const deleteRow = () => {
    if (selectedCell && selectedCell.row < data.length) {
      const newData = data.filter((_, index) => index !== selectedCell.row);
      setData(newData);
      setRowCount(rowCount - 1);
      setSelectedCell(null);
    }
  };

  // Add new column
  const addColumn = () => {
    const newColKey = generateColumnKey(columnHeaders.length);
    setColumnHeaders([...columnHeaders, newColKey]);
    const newData = data.map(row => ({
      ...row,
      [newColKey]: ''
    }));
    setData(newData);
  };

  // Generate column key (A, B, ..., Z, AA, AB, ...)
  const generateColumnKey = (index) => {
    let result = '';
    while (index >= 0) {
      result = String.fromCharCode(65 + (index % 26)) + result;
      index = Math.floor(index / 26) - 1;
    }
    return result;
  };

  // Delete selected column
  const deleteColumn = () => {
    if (selectedCell && columnHeaders.length > 1) {
      const colIndex = columnHeaders.indexOf(selectedCell.col);
      const newHeaders = columnHeaders.filter((_, index) => index !== colIndex);
      setColumnHeaders(newHeaders);
      const newData = data.map(row => {
        const newRow = { ...row };
        delete newRow[selectedCell.col];
        return newRow;
      });
      setData(newData);
      setSelectedCell(null);
    }
  };

  // Handle context menu
  const handleContextMenu = (e, rowIndex, colKey) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuType('cell');
    setShowContextMenu(true);
    setSelectedCell({ row: rowIndex, col: colKey });
  };

  // Handle column header context menu
  const handleColumnContextMenu = (e, colKey) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuType('column');
    setShowContextMenu(true);
    setSelectedCell({ row: 0, col: colKey });
  };

  // Handle row number context menu
  const handleRowContextMenu = (e, rowIndex) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuType('row');
    setShowContextMenu(true);
    setSelectedCell({ row: rowIndex, col: columnHeaders[0] });
  };

  // Close context menu
  const closeContextMenu = () => {
    setShowContextMenu(false);
  };

  // Handle mouse down for selection
  const handleMouseDown = (e, rowIndex, colKey) => {
    if (readOnly) return;
    setIsDragging(true);
    setDragStart({ row: rowIndex, col: colKey });
    setDragEnd({ row: rowIndex, col: colKey });
    setSelectedCell({ row: rowIndex, col: colKey });
  };

  // Handle mouse move for selection
  const handleMouseMove = (e, rowIndex, colKey) => {
    if (isDragging && dragStart) {
      setDragEnd({ row: rowIndex, col: colKey });
      setSelectedRange({
        start: dragStart,
        end: { row: rowIndex, col: colKey }
      });
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Get cell value with memoization
  const getCellValue = useCallback((rowIndex, colKey) => {
    return data[rowIndex]?.[colKey] || '';
  }, [data]);

  // Calculate column width based on content
  const calculateColumnWidth = useCallback((colKey) => {
    if (columnWidths[colKey]) return columnWidths[colKey];

    let maxWidth = 80; // Minimum width
    for (let i = 0; i < Math.min(data.length, 100); i++) {
      const cellValue = getCellValue(i, colKey);
      if (cellValue) {
        const width = Math.max(cellValue.length * 8 + 16, 80);
        maxWidth = Math.max(maxWidth, width);
      }
    }
    return Math.min(maxWidth, 300); // Max width
  }, [data, columnWidths, getCellValue]);

  // -----------------------------
  // Vertical virtualization (rows)
  // -----------------------------
  const ROW_HEIGHT = 32; // px (matches h-8)
  const BUFFER_ROWS = 10;

  const [visibleStartRow, setVisibleStartRow] = useState(0);
  const [visibleRowCount, setVisibleRowCount] = useState(50);

  // Measure container to compute visible rows on mount and resize
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const compute = () => {
      const count = Math.ceil(container.clientHeight / ROW_HEIGHT) + BUFFER_ROWS;
      setVisibleRowCount(count);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Throttled scroll handler (60fps)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const start = Math.floor(container.scrollTop / ROW_HEIGHT);
        setVisibleStartRow(Math.max(0, Math.min(start, Math.max(0, rowCount - 1))));
      });
    };
    container.addEventListener('scroll', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [rowCount]);

  const visibleEndRow = Math.min(rowCount, visibleStartRow + visibleRowCount);
  const topSpacerHeight = visibleStartRow * ROW_HEIGHT;
  const bottomSpacerHeight = Math.max(0, (rowCount - visibleEndRow) * ROW_HEIGHT);


  // Check if cell is selected
  const isCellSelected = (rowIndex, colKey) => {
    const cellKey = `${rowIndex}-${colKey}`;

    // Check if cell is in selectedCells set (Ctrl+Click selection)
    if (selectedCells.has(cellKey)) {
      return true;
    }

    // Check if cell is in selected range
    if (selectedRange) {
      const { start, end } = selectedRange;
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));
      const maxCol = Math.max(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));
      return rowIndex >= minRow && rowIndex <= maxRow &&
        columnHeaders.indexOf(colKey) >= minCol && columnHeaders.indexOf(colKey) <= maxCol;
    }

    // Check if cell is the currently selected cell
    return selectedCell?.row === rowIndex && selectedCell?.col === colKey;
  };

  // Check if cell is being edited
  const isCellEditing = (rowIndex, colKey) => {
    return editingCell?.row === rowIndex && editingCell?.col === colKey;
  };

  // Copy functionality with range support
  const handleCopy = useCallback(() => {
    if (selectedCell) {
      let copyText = '';

      if (selectedRange) {
        // Copy range of cells
        const { start, end } = selectedRange;
        const minRow = Math.min(start.row, end.row);
        const maxRow = Math.max(start.row, end.row);
        const minColIndex = Math.min(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));
        const maxColIndex = Math.max(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));

        const rows = [];
        for (let row = minRow; row <= maxRow; row++) {
          const cells = [];
          for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
            const col = columnHeaders[colIndex];
            cells.push(getCellValue(row, col));
          }
          rows.push(cells.join('\t'));
        }
        copyText = rows.join('\n');
      } else {
        // Copy single cell
        copyText = getCellValue(selectedCell.row, selectedCell.col);
      }

      setCopiedData({
        value: copyText,
        row: selectedCell.row,
        col: selectedCell.col
      });

      // Copy to clipboard
      navigator.clipboard.writeText(copyText).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = copyText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      });
    }
  }, [selectedCell, selectedRange, getCellValue, columnHeaders]);

  // Parse clipboard data for multi-cell paste
  const parseClipboardData = useCallback((text) => {
    // Split by newlines to get rows
    const rows = text.split(/\r?\n/).filter(row => row.trim() !== '');

    // Split each row by tabs to get cells
    const parsedData = rows.map(row => row.split('\t'));

    return parsedData;
  }, []);

  // Paste functionality with multi-cell support and replacement
  const handlePaste = useCallback(async () => {
    if (selectedCell && !readOnly) {
      try {
        const clipboardText = await navigator.clipboard.readText();
        const parsedData = parseClipboardData(clipboardText);

        const newData = [...data];

        if (parsedData.length === 1 && parsedData[0].length === 1) {
          // Single cell paste - replace selected cells
          if (selectedCells.size > 0) {
            // Replace all selected cells with the same value
            selectedCells.forEach(cellKey => {
              const [rowIndex, colKey] = cellKey.split('-');
              const row = parseInt(rowIndex);
              if (!newData[row]) newData[row] = {};
              newData[row][colKey] = parsedData[0][0];
            });
          } else if (selectedRange) {
            // Replace range with the same value
            const { start, end } = selectedRange;
            const minRow = Math.min(start.row, end.row);
            const maxRow = Math.max(start.row, end.row);
            const minCol = Math.min(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));
            const maxCol = Math.max(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));

            for (let row = minRow; row <= maxRow; row++) {
              if (!newData[row]) newData[row] = {};
              for (let colIndex = minCol; colIndex <= maxCol; colIndex++) {
                const col = columnHeaders[colIndex];
                newData[row][col] = parsedData[0][0];
              }
            }
          } else {
            // Single cell replacement
            if (!newData[selectedCell.row]) newData[selectedCell.row] = {};
            newData[selectedCell.row][selectedCell.col] = parsedData[0][0];
          }
        } else {
          // Multi-cell paste
          parsedData.forEach((row, rowIndex) => {
            const targetRow = selectedCell.row + rowIndex;
            if (targetRow < data.length) {
              if (!newData[targetRow]) {
                newData[targetRow] = {};
              }

              row.forEach((cellValue, colIndex) => {
                const targetColIndex = columnHeaders.indexOf(selectedCell.col) + colIndex;
                const targetCol = columnHeaders[targetColIndex];
                if (targetCol) {
                  newData[targetRow][targetCol] = cellValue;
                }
              });
            }
          });
        }

        setData(newData);
        onDataChange?.(newData);

        // Show paste preview briefly
        setPastePreview({
          rows: parsedData.length,
          cols: parsedData[0]?.length || 0,
          startRow: selectedCell.row,
          startCol: selectedCell.col
        });

        setTimeout(() => setPastePreview(null), 2000);
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        document.body.appendChild(textArea);
        textArea.focus();
        document.execCommand('paste');
        const clipboardText = textArea.value;
        document.body.removeChild(textArea);

        if (clipboardText) {
          const parsedData = parseClipboardData(clipboardText);
          const newData = [...data];

          if (parsedData.length === 1 && parsedData[0].length === 1) {
            // Single cell paste - replace selected cells
            if (selectedCells.size > 0) {
              selectedCells.forEach(cellKey => {
                const [rowIndex, colKey] = cellKey.split('-');
                const row = parseInt(rowIndex);
                if (!newData[row]) newData[row] = {};
                newData[row][colKey] = parsedData[0][0];
              });
            } else if (selectedRange) {
              const { start, end } = selectedRange;
              const minRow = Math.min(start.row, end.row);
              const maxRow = Math.max(start.row, end.row);
              const minCol = Math.min(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));
              const maxCol = Math.max(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));

              for (let row = minRow; row <= maxRow; row++) {
                if (!newData[row]) newData[row] = {};
                for (let colIndex = minCol; colIndex <= maxCol; colIndex++) {
                  const col = columnHeaders[colIndex];
                  newData[row][col] = parsedData[0][0];
                }
              }
            } else {
              if (!newData[selectedCell.row]) newData[selectedCell.row] = {};
              newData[selectedCell.row][selectedCell.col] = parsedData[0][0];
            }
          } else {
            // Multi-cell paste
            parsedData.forEach((row, rowIndex) => {
              const targetRow = selectedCell.row + rowIndex;
              if (targetRow < data.length) {
                if (!newData[targetRow]) {
                  newData[targetRow] = {};
                }

                row.forEach((cellValue, colIndex) => {
                  const targetColIndex = columnHeaders.indexOf(selectedCell.col) + colIndex;
                  const targetCol = columnHeaders[targetColIndex];
                  if (targetCol) {
                    newData[targetRow][targetCol] = cellValue;
                  }
                });
              }
            });
          }

          setData(newData);
          onDataChange?.(newData);

          setPastePreview({
            rows: parsedData.length,
            cols: parsedData[0]?.length || 0,
            startRow: selectedCell.row,
            startCol: selectedCell.col
          });

          setTimeout(() => setPastePreview(null), 2000);
        }
      }
    }
  }, [selectedCell, selectedCells, selectedRange, readOnly, data, columnHeaders, parseClipboardData, onDataChange]);

  // Handle delete/backspace for selected cells
  const handleDelete = useCallback(() => {
    if (readOnly) return;

    const newData = [...data];

    if (selectedCells.size > 0) {
      // Delete multiple selected cells (Ctrl+Click selection)
      selectedCells.forEach(cellKey => {
        const [rowIndex, colKey] = cellKey.split('-');
        const row = parseInt(rowIndex);
        if (newData[row]) {
          newData[row][colKey] = '';
        }
      });
    } else if (selectedRange) {
      // Delete range of cells
      const { start, end } = selectedRange;
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));
      const maxCol = Math.max(columnHeaders.indexOf(start.col), columnHeaders.indexOf(end.col));

      for (let row = minRow; row <= maxRow; row++) {
        if (!newData[row]) newData[row] = {};
        for (let colIndex = minCol; colIndex <= maxCol; colIndex++) {
          const col = columnHeaders[colIndex];
          newData[row][col] = '';
        }
      }
    } else if (selectedCell) {
      // Delete single cell
      if (newData[selectedCell.row]) {
        newData[selectedCell.row][selectedCell.col] = '';
      }
    }

    setData(newData);
    onDataChange?.(newData);
  }, [readOnly, data, selectedCells, selectedRange, selectedCell, columnHeaders, onDataChange]);

  // Handle arrow key navigation with Shift selection
  const handleArrowKeyNavigation = useCallback((e) => {
    if (!selectedCell) return;

    const { row, col } = selectedCell;
    const currentColIndex = columnHeaders.indexOf(col);
    let newRow = row;
    let newColIndex = currentColIndex;
    let extendWholeRow = false;
    let extendWholeCol = false;

    switch (e.key) {
      case 'ArrowUp':
        newRow = Math.max(0, row - 1);
        break;
      case 'ArrowDown':
        newRow = Math.min(data.length - 1, row + 1);
        break;
      case 'ArrowLeft':
        newColIndex = Math.max(0, currentColIndex - 1);
        break;
      case 'ArrowRight':
        newColIndex = Math.min(columnHeaders.length - 1, currentColIndex + 1);
        break;
      case 'Tab':
        if (e.shiftKey) {
          // Shift + Tab: Move left
          newColIndex = Math.max(0, currentColIndex - 1);
        } else {
          // Tab: Move right
          newColIndex = Math.min(columnHeaders.length - 1, currentColIndex + 1);
        }
        break;
      case 'Enter':
        if (e.shiftKey) {
          // Shift + Enter: Move up
          newRow = Math.max(0, row - 1);
        } else {
          // Enter: Move down
          newRow = Math.min(data.length - 1, row + 1);
        }
        break;
      default:
        return;
    }

    const newCol = columnHeaders[newColIndex];

    if (e.shiftKey && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      // If current selection is an entire row or column, extend entire rows/columns
      const isRowSelection = selectedRange && selectedRange.start.row === selectedRange.end.row && selectedRange.start.col === columnHeaders[0] && selectedRange.end.col === columnHeaders[columnHeaders.length - 1];
      const isColSelection = selectedRange && selectedRange.start.col === selectedRange.end.col && selectedRange.start.row === 0 && selectedRange.end.row === (data.length - 1);

      if (isRowSelection) {
        // Extend entire row selection horizontally shouldn't change; vertically should add rows
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          const startRow = selectedRange.start.row;
          const endRow = selectedRange.end.row;
          const nextRow = e.key === 'ArrowUp' ? Math.max(0, startRow - 1) : Math.min(data.length - 1, endRow + 1);
          setSelectedRange({
            start: { row: Math.min(nextRow, startRow), col: columnHeaders[0] },
            end: { row: Math.max(nextRow, endRow), col: columnHeaders[columnHeaders.length - 1] }
          });
          setSelectedCells(new Set());
          e.preventDefault();
          return;
        }
      }

      if (isColSelection) {
        // Extend entire column selection horizontally should add columns; vertically shouldn't change
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          const colIdx = columnHeaders.indexOf(selectedRange.start.col);
          const newIdx = e.key === 'ArrowLeft' ? Math.max(0, colIdx - 1) : Math.min(columnHeaders.length - 1, colIdx + 1);
          const newColKey = columnHeaders[newIdx];
          setSelectedRange({
            start: { row: 0, col: Math.min(columnHeaders[colIdx], newColKey) === columnHeaders[colIdx] ? newColKey : selectedRange.start.col },
            end: { row: data.length - 1, col: Math.max(columnHeaders[colIdx], newColKey) === newColKey ? newColKey : selectedRange.end.col }
          });
          setSelectedCells(new Set());
          e.preventDefault();
          return;
        }
      }

      // Default: extend cell range
      if (selectedRange) {
        setSelectedRange({
          start: selectedRange.start,
          end: { row: newRow, col: newCol }
        });
      } else {
        setSelectedRange({
          start: { row, col },
          end: { row: newRow, col: newCol }
        });
      }
      setSelectedCells(new Set());
    } else {
      // Normal navigation: Move selection
      setSelectedCell({ row: newRow, col: newCol });
      setSelectedRange(null);
      setSelectedCells(new Set([`${newRow}-${newCol}`]));
    }

    e.preventDefault();
  }, [selectedCell, columnHeaders, data.length, selectedRange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Shift+Space: select entire row
    if (e.key === ' ' && e.shiftKey && selectedCell) {
      e.preventDefault();
      const r = selectedCell.row;
      setSelectedRange({
        start: { row: r, col: columnHeaders[0] },
        end: { row: r, col: columnHeaders[columnHeaders.length - 1] }
      });
      setSelectedCells(new Set());
      return;
    }
    // Ctrl+Space / Meta+Space: select entire column
    if (e.key === ' ' && (e.ctrlKey || e.metaKey) && selectedCell) {
      e.preventDefault();
      const c = selectedCell.col;
      setSelectedRange({
        start: { row: 0, col: c },
        end: { row: data.length - 1, col: c }
      });
      setSelectedCells(new Set());
      return;
    }
    // Handle navigation keys first
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
      handleArrowKeyNavigation(e);
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'c':
          e.preventDefault();
          handleCopy();
          break;
        case 'v':
          e.preventDefault();
          handlePaste();
          break;
        case 'a':
          e.preventDefault();
          // Select all cells
          setSelectedRange({
            start: { row: 0, col: columnHeaders[0] },
            end: { row: data.length - 1, col: columnHeaders[columnHeaders.length - 1] }
          });
          setSelectedCells(new Set());
          break;
      }
    } else {
      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          handleDelete();
          break;
      }
    }
  }, [handleArrowKeyNavigation, handleCopy, handlePaste, handleDelete, columnHeaders, data.length]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Handle Ctrl and Shift key states
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(true);
      }
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(false);
      }
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);


  return (
    <div className="spreadsheet-container" ref={spreadsheetRef}>
      {/* Toolbar */}
      <div className="bg-gray-100 border-b p-2 flex gap-2 items-center justify-between">
        <span className="font-semibold text-sm">{sheetName}</span>
        {pastePreview && (
          <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
            Pasted {pastePreview.rows}×{pastePreview.cols} cells
          </div>
        )}
      </div>

      {/* Spreadsheet */}
      <div ref={scrollContainerRef} className="overflow-auto border" style={{ height: 'calc(100vh - 200px)' }}>
        <table className="border-collapse" ref={tableRef} style={{ tableLayout: 'fixed', width: 'max-content' }}>
          <thead className="sticky top-0 z-10">
            <tr>
              <th
                className="h-8 bg-gray-100 border border-gray-300 text-xs font-medium sticky left-0 z-20"
                style={{ width: '48px', minWidth: '48px' }}
              ></th>
              {columnHeaders.map((col) => (
                <th
                  key={col}
                  className="h-8 bg-gray-100 border border-gray-300 text-xs font-medium text-center cursor-pointer hover:bg-gray-200"
                  style={{
                    width: `${calculateColumnWidth(col)}px`,
                    minWidth: `${calculateColumnWidth(col)}px`
                  }}
                  onClick={() => handleColumnHeaderClick(col)}
                  onContextMenu={(e) => handleColumnContextMenu(e, col)}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Top spacer to keep scroll position */}
            {topSpacerHeight > 0 && (
              <tr>
                <td style={{ height: `${topSpacerHeight}px`, padding: 0, border: 'none' }} colSpan={columnHeaders.length + 1}></td>
              </tr>
            )}

            {data.slice(visibleStartRow, visibleEndRow).map((row, sliceIndex) => {
              const rowIndex = visibleStartRow + sliceIndex;
              return (
              <tr key={rowIndex}>
                <td
                  className="h-8 bg-gray-100 border border-gray-300 text-xs font-medium text-center cursor-pointer hover:bg-gray-200 sticky left-0 z-10"
                  style={{ width: '48px', minWidth: '48px' }}
                  onClick={() => handleRowHeaderClick(rowIndex)}
                  onContextMenu={(e) => handleRowContextMenu(e, rowIndex)}
                >
                  {rowIndex + 1}
                </td>
                {columnHeaders.map((col) => (
                  <td
                    key={col}
                    className={`h-8 border border-gray-300 text-xs relative ${isCellSelected(rowIndex, col)
                      ? 'bg-blue-100 border-blue-500'
                      : 'hover:bg-gray-50'
                      }`}
                    style={{
                      width: `${calculateColumnWidth(col)}px`,
                      minWidth: `${calculateColumnWidth(col)}px`
                    }}
                    onClick={(e) => handleCellClick(e, rowIndex, col)}
                    onDoubleClick={() => handleCellDoubleClick(rowIndex, col)}
                    onMouseDown={(e) => handleMouseDown(e, rowIndex, col)}
                    onMouseMove={(e) => handleMouseMove(e, rowIndex, col)}
                    onMouseUp={handleMouseUp}
                    onContextMenu={(e) => handleContextMenu(e, rowIndex, col)}
                  >
                    {isCellEditing(rowIndex, col) ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyPress}
                        className="w-full h-full border-0 outline-none bg-transparent px-1"
                      />
                    ) : (
                      <span className="block w-full h-full px-1 py-1 overflow-hidden whitespace-nowrap">
                        {getCellValue(rowIndex, col)}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            )})}

            {/* Bottom spacer to fill remaining height */}
            {bottomSpacerHeight > 0 && (
              <tr>
                <td style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: 'none' }} colSpan={columnHeaders.length + 1}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="fixed bg-white border border-gray-300 shadow-lg rounded py-1 z-50"
          style={{
            left: contextMenuPos.x,
            top: contextMenuPos.y,
          }}
          onClick={closeContextMenu}
        >
          {contextMenuType === 'cell' && (
            <>
              <button
                className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                onClick={() => {
                  if (selectedCell) {
                    handleCellDoubleClick(selectedCell.row, selectedCell.col);
                  }
                  closeContextMenu();
                }}
                disabled={readOnly}
              >
                Edit
              </button>
              <button
                className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                onClick={() => {
                  handleCopy();
                  closeContextMenu();
                }}
              >
                Copy
              </button>
              <button
                className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                onClick={() => {
                  handlePaste();
                  closeContextMenu();
                }}
                disabled={readOnly}
              >
                Paste
              </button>
              <button
                className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                onClick={() => {
                  if (selectedCell) {
                    handleCellChange(selectedCell.row, selectedCell.col, '');
                  }
                  closeContextMenu();
                }}
                disabled={readOnly}
              >
                Clear
              </button>
              <hr className="my-1" />
              <button
                className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                onClick={() => {
                  deleteRow();
                  closeContextMenu();
                }}
                disabled={readOnly}
              >
                Delete Row
              </button>
            </>
          )}
          {contextMenuType === 'column' && (
            <button
              className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
              onClick={() => {
                deleteColumn();
                closeContextMenu();
              }}
              disabled={readOnly || columnHeaders.length <= 1}
            >
              Delete Column
            </button>
          )}
          {contextMenuType === 'row' && (
            <button
              className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
              onClick={() => {
                deleteRow();
                closeContextMenu();
              }}
              disabled={readOnly}
            >
              Delete Row
            </button>
          )}
        </div>
      )}

      {/* Click outside to close context menu */}
      {showContextMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeContextMenu}
        />
      )}
    </div>
  );
};

export default Spreadsheet;
